import { useMemo } from 'react';
import { useQuery } from 'react-query';

import {
  getAICompetitiveIntelligence,
  getCompanyAICIStats,
  getCompanySpeedToLead,
  getRecommendationDetails,
  getRecommendations,
} from 'apis/RecommendationsAPI';
import { SPEED_TO_LEAD_CONFIG_ID, SPEED_TO_LEAD_QUERY_KEY } from 'components/SpeedToLead/constants';
import { getOverallInsight } from 'components/SpeedToLead/speedToLeadOverallUtils';
import { canAccessAICompetitiveIntelligence, canAccessAISpeedToLead } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { AICI_ANALYSIS_SCOPE, AICIAnalysisScope } from './constants';
import useRecommendationsContext from './RecommendationsContext';

type Params = {
  communityId?: number;
  scope: AICIAnalysisScope;
};

const useAICIAnalysisAvailability = ({ communityId, scope }: Params) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { communityMap, selectedCommunity } = useAppSelector((state) => state.global);
  const { company } = useRecommendationsContext();
  const isCompanyScope = scope === AICI_ANALYSIS_SCOPE.COMPANY;
  const canAccessOverall = canAccessAICompetitiveIntelligence(currentUser);
  const canAccessSpeedToLead = canAccessAISpeedToLead(currentUser);

  const { data: communityOverall, isLoading: communityOverallLoading } = useQuery({
    queryKey: ['ai-competitive-intelligence', communityId],
    queryFn: () => getAICompetitiveIntelligence(communityId ?? -1),
    enabled: Boolean(!isCompanyScope && canAccessOverall && communityId),
    refetchOnWindowFocus: false,
  });
  const { data: companyOverall, isLoading: companyOverallLoading } = useQuery({
    queryKey: ['ai-competitive-intelligence-company-stats', company?.id],
    queryFn: () => getCompanyAICIStats(company?.id ?? -1),
    enabled: Boolean(isCompanyScope && canAccessOverall && company?.id),
    refetchOnWindowFocus: false,
  });
  const { data: communitySpeedToLead = [], isLoading: communitySpeedToLeadLoading } = useQuery({
    queryKey: [SPEED_TO_LEAD_QUERY_KEY.RECOMMENDATIONS, company?.id, communityId],
    queryFn: () =>
      getRecommendations(company?.id ?? -1, {
        communities: communityId ? [communityId] : [],
        speed_to_lead: true,
      }),
    enabled: Boolean(!isCompanyScope && canAccessSpeedToLead && company?.id && communityId),
    refetchOnWindowFocus: false,
  });
  const { data: companySpeedToLead, isLoading: companySpeedToLeadLoading } = useQuery({
    queryKey: ['ai-competitive-intelligence-company-speed-to-lead', company?.id],
    queryFn: () => getCompanySpeedToLead(company?.id ?? -1),
    enabled: Boolean(isCompanyScope && canAccessSpeedToLead && company?.id),
    refetchOnWindowFocus: false,
  });
  const speedToLeadRecommendation = useMemo(
    () => communitySpeedToLead.find((recommendation) => recommendation.config.id === SPEED_TO_LEAD_CONFIG_ID.OVERALL),
    [communitySpeedToLead]
  );
  const { data: communitySpeedToLeadDetails, isLoading: communitySpeedToLeadDetailsLoading } = useQuery({
    queryKey: [SPEED_TO_LEAD_QUERY_KEY.DETAILS, speedToLeadRecommendation?.id],
    queryFn: () => getRecommendationDetails(speedToLeadRecommendation?.id ?? -1),
    enabled: Boolean(!isCompanyScope && speedToLeadRecommendation?.id),
    refetchOnWindowFocus: false,
  });
  const communitySpeedToLeadRanking = useMemo(() => {
    const insight = getOverallInsight(communitySpeedToLeadDetails, communityId, selectedCommunity?.name);
    return insight?.rankingRows.find((row) => row.community_id === communityId)?.rank ?? insight?.data.ranking ?? null;
  }, [communityId, communitySpeedToLeadDetails, selectedCommunity?.name]);
  const competitorsCount = communityId ? (communityMap[communityId]?.competitors?.length ?? 0) : 0;

  const hasOverallAnalysis = canAccessOverall
    ? isCompanyScope
      ? Boolean(companyOverall?.categories.length)
      : Boolean(communityOverall?.ranking != null && communityOverall?.summary != null)
    : false;
  const hasSpeedToLeadAnalysis = canAccessSpeedToLead
    ? isCompanyScope
      ? Boolean(companySpeedToLead?.analyzed_communities)
      : Boolean(speedToLeadRecommendation)
    : false;
  const isLoading = isCompanyScope
    ? (canAccessOverall && companyOverallLoading) || (canAccessSpeedToLead && companySpeedToLeadLoading)
    : (canAccessOverall && communityOverallLoading) ||
      (canAccessSpeedToLead && (communitySpeedToLeadLoading || communitySpeedToLeadDetailsLoading));

  return {
    analyzedSpeedToLeadCommunities: companySpeedToLead?.analyzed_communities ?? 0,
    canAccessOverall,
    competitorsCount,
    hasOverallAnalysis,
    hasSpeedToLeadAnalysis,
    isLoading,
    overallRanking: communityOverall?.ranking ?? null,
    speedToLeadRanking: communitySpeedToLeadRanking,
  };
};

export default useAICIAnalysisAvailability;
